import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TextArea } from 'components/Base/Input';
import { Text } from 'components/Base/Typography';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import { EditPencilOutline } from '../../../../../../../assets/icon/icon';
import {
  DisplayContainer,
  EditableContainer,
  EditIcon,
} from './TaskTitleInput.styles';

interface TaskTitleInputProps {
  value: string;
  rowId?: string;
  isNewRow?: boolean;
  disabled?: boolean;
  onUpdate?: (rowId: string, field: string, value: string) => void;
  onSave?: (rowId: string, taskTitle: string) => void;
  onCancel?: (rowId: string) => void;
}

const isWithinAnyElement = (nodeList: NodeListOf<Element>, target: Node) => {
  return Array.from(nodeList).some(element => element.contains(target));
};

const TaskTitleInput: React.FC<TaskTitleInputProps> = ({
  value,
  rowId,
  isNewRow = false,
  disabled = false,
  onUpdate,
  onSave,
  onCancel,
}) => {
  const { showToastNotification } = useShowToastNotification();

  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(isNewRow);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = useCallback(() => {
    if (localValue.trim()) {
      if (!rowId) return;

      console.log('TEST');

      onSave?.(rowId, localValue.trim());
      setIsEditing(false);
    } else {
      console.log('TEST 2');

      showToastNotification({
        type: ENotificationType.ERROR,
        message: 'Task title cannot be empty.',
      });
    }
  }, [localValue, rowId, onSave, showToastNotification]);

  const handleCancel = useCallback(() => {
    setLocalValue(value);
    setIsEditing(false);

    if (!rowId) return;

    onCancel?.(rowId);
  }, [value, rowId, onCancel]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    if (!rowId) return;

    onUpdate?.(rowId, 'taskTitle', newValue);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setIsEditing(true);
  };

  const handleDisplayClick = () => {
    if (disabled) return;
    setIsEditing(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const toastElement = document.querySelector('.ant-notification');
      const addTaskBtns = document.querySelectorAll('[data-cy="add-task-btn"]');
      const deleteTaskBtns = document.querySelectorAll(
        '[data-cy="delete-task-btn"]',
      );
      const editTacticBtns = document.querySelectorAll(
        '[data-cy="edit-tactic-btn"]',
      );
      const deleteTacticBtns = document.querySelectorAll(
        '[data-cy="delete-tactic-btn"]',
      );
      const taskAssigneeBtns = document.querySelectorAll(
        '[data-cy="task-assignee-button"]',
      );
      const taskStatusDropdown = document.querySelectorAll(
        '[data-cy="task-status-dropdown"]',
      );
      const addDateBtns = document.querySelectorAll('[data-cy="add-date-btn"]');
      const expandRowBtns = document.querySelectorAll(
        '.ant-table-row-expand-icon-cell',
      );
      const expandIconBtns = document.querySelectorAll(
        '.ant-table-row-expand-icon-cell svg',
      );
      const datePicker = document.querySelector('.ant-picker');
      const modalElement = document.querySelector('.ant-modal-root');

      const buttonDropdown = document.querySelectorAll('.button-dropdown');

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !(toastElement && toastElement.contains(event.target as Node)) &&
        !isWithinAnyElement(addTaskBtns, event.target as Node) &&
        !isWithinAnyElement(deleteTaskBtns, event.target as Node) &&
        !isWithinAnyElement(editTacticBtns, event.target as Node) &&
        !isWithinAnyElement(deleteTacticBtns, event.target as Node) &&
        !isWithinAnyElement(taskAssigneeBtns, event.target as Node) &&
        !isWithinAnyElement(taskStatusDropdown, event.target as Node) &&
        !isWithinAnyElement(addDateBtns, event.target as Node) &&
        !isWithinAnyElement(expandRowBtns, event.target as Node) &&
        !isWithinAnyElement(expandIconBtns, event.target as Node) &&
        !isWithinAnyElement(buttonDropdown, event.target as Node) &&
        !(datePicker && datePicker.contains(event.target as Node)) &&
        !(modalElement && modalElement.contains(event.target as Node))
      ) {
        handleSave();
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, handleSave]);

  if (isEditing) {
    return (
      <EditableContainer ref={containerRef}>
        <TextArea
          ref={textAreaRef}
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          placeholder="Enter task title"
          autoSize
          data-cy="task-title-input"
        />
      </EditableContainer>
    );
  }

  return (
    <EditableContainer ref={containerRef}>
      <DisplayContainer
        onClick={handleDisplayClick}
        data-cy="task-title-display"
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <div>
          {localValue ? (
            <Text>{localValue}</Text>
          ) : (
            <Text>Click to add task title</Text>
          )}
        </div>
        {!disabled && (
          <EditIcon className="edit-icon" onClick={handleEditClick}>
            <EditPencilOutline size={14} />
          </EditIcon>
        )}
      </DisplayContainer>
    </EditableContainer>
  );
};

export default TaskTitleInput;
